'use client';

import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { InView, Stagger, StaggerChild } from '@/components/motion/InView';
import { localized, type HomeContentConfig, type RestaurantLocale } from '@restaurant/config';
import styles from './FeaturedAnnouncementsSection.module.css';

interface AnnouncementCard {
  id: string;
  slug: string;
  category: string;
  title: { fr: string; en: string; es: string };
  excerpt: { fr: string; en: string; es: string };
  imageUrl: string | null;
  imageAlt: { fr: string; en: string; es: string };
  hasSinglePage: boolean;
}

interface FeaturedAnnouncementsSectionProps {
  content: HomeContentConfig['highlights'];
  locale: RestaurantLocale;
  announcements: AnnouncementCard[];
}

const CATEGORY_LABELS: Record<string, { fr: string; en: string; es: string }> = {
  EVENT: { fr: 'Événement', en: 'Event', es: 'Evento' },
  DAILY_SPECIAL: { fr: 'Plat du jour', en: 'Dish of the day', es: 'Plato del día' },
  WEEK_THEME: { fr: 'Cette semaine', en: 'This week', es: 'Esta semana' },
  OTHER: { fr: 'Actualité', en: 'News', es: 'Actualidad' },
};

const EMPTY_STATE = {
  fr: {
    eyebrow: 'Bientôt',
    title: 'Les actualités du B Brasserie arrivent prochainement.',
    body: "Notre équipe prépare avec soin les prochaines soirées, plats de saison et rendez-vous gourmands. Revenez très vite — ils seront annoncés ici en avant-première.",
  },
  en: {
    eyebrow: 'Coming soon',
    title: 'News from B Brasserie is on its way.',
    body: "Our team is carefully preparing the next events, seasonal dishes, and gatherings. Come back soon — they'll be unveiled here first.",
  },
  es: {
    eyebrow: 'Muy pronto',
    title: 'Las novedades de B Brasserie están en camino.',
    body: 'Nuestro equipo prepara con esmero los próximos eventos, platos de temporada y citas gourmet. Vuelve pronto — se anunciarán aquí en primicia.',
  },
} as const;

export function FeaturedAnnouncementsSection({
  content,
  locale,
  announcements,
}: FeaturedAnnouncementsSectionProps) {
  const hasAnnouncements = announcements.length > 0;

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <InView className={styles.heading} y={24} delay={0}>
          <h2 className={styles.title}>{localized(content.title, locale)}</h2>
          <p className={styles.subtitle}>{localized(content.subtitle, locale)}</p>
        </InView>

        {hasAnnouncements ? (
          <Stagger className={styles.list} stagger={0.1} delayChildren={0.05}>
            {announcements.map((announcement) => {
              const categoryLabel =
                CATEGORY_LABELS[announcement.category] ?? CATEGORY_LABELS.OTHER;

              const cardContent = (
                <>
                  <div className={`${styles.imageFrame}${!announcement.imageUrl ? ` ${styles.imageFrameEmpty}` : ''}`}>
                    {announcement.imageUrl ? (
                      <Image
                        className={styles.image}
                        src={announcement.imageUrl}
                        alt={localized(announcement.imageAlt, locale)}
                        fill
                        quality={70}
                        sizes="(max-width: 560px) 80px, 144px"
                      />
                    ) : null}
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.eyebrow}>{localized(categoryLabel, locale)}</span>
                    <h3 className={styles.cardTitle}>{localized(announcement.title, locale)}</h3>
                    <p className={styles.cardText}>{localized(announcement.excerpt, locale)}</p>
                  </div>
                  <span className={styles.cardArrow} aria-hidden="true">→</span>
                </>
              );

              return (
                <StaggerChild key={announcement.id} y={20} duration={0.6}>
                  {announcement.hasSinglePage ? (
                    <Link
                      href={{
                        pathname: '/en-ce-moment/[slug]',
                        params: { slug: announcement.slug },
                      }}
                      className={styles.card}
                    >
                      {cardContent}
                    </Link>
                  ) : (
                    <article className={`${styles.card} ${styles.cardStatic}`}>{cardContent}</article>
                  )}
                </StaggerChild>
              );
            })}
          </Stagger>
        ) : (
          <InView className={styles.emptyState} y={20} delay={0.1}>
            <p className={styles.emptyEyebrow}>{EMPTY_STATE[locale].eyebrow}</p>
            <p className={styles.emptyTitle}>{EMPTY_STATE[locale].title}</p>
            <p className={styles.emptyBody}>{EMPTY_STATE[locale].body}</p>
          </InView>
        )}

        {hasAnnouncements ? (
          <InView className={styles.footerAction} y={16} delay={0.1}>
            <Link href="/en-ce-moment" className={styles.allLink}>
              {localized(content.allCta, locale)}
            </Link>
          </InView>
        ) : null}
      </div>
    </section>
  );
}
